// src/services/serpapi-google-trends.ts
import axios from 'axios';
import { GoogleTrendsData, TimeframeParams } from '../types';
import { withCache, CACHE_KEYS, setCachedData } from './cache';

/**
 * SerpAPI Google Trends Integration
 * 
 * This module provides a function to fetch Google Trends data using SerpAPI.
 * It includes caching, error handling, and proper data formatting to match
 * the dashboard's expected data structure.
 */

// Cache configuration for Google Trends (12 hours = 43200 seconds)
const GOOGLE_TRENDS_TTL = 12 * 60 * 60; // 12 hours in seconds

// Fallback cache for storing results when Upstash is unavailable
interface CacheEntry {
  timestamp: number;
  data: GoogleTrendsData;
}

const fallbackCache = new Map<string, CacheEntry>();
const FALLBACK_CACHE_TTL = 43200000; // 12 hours in milliseconds

// Default to 7-day timeframe to minimize API usage
const DEFAULT_TIMEFRAME = 'now 7-d';

/**
 * Generate cache key based on parameters
 */
const getCacheKey = (term: string, timeframe?: string): string => {
  return `trends_${term}_${timeframe}`;
};

/**
 * Check if cache entry is still valid
 */
const isCacheValid = (cacheEntry?: CacheEntry): boolean => {
  return !!cacheEntry && (Date.now() - cacheEntry.timestamp) < FALLBACK_CACHE_TTL;
};

/**
 * Generate error state for Google Trends when API is unavailable
 * Returns null to indicate no data available rather than misleading fallback
 */
const generateFallbackGoogleTrendsData = (term: string): GoogleTrendsData | null => {
  // Don't return fake data - return null to show error state
  console.warn(`⚠️ Google Trends API unavailable for "${term}" - showing error state`);
  return null;
};

/**
 * Fetch Google Trends data using SerpAPI
 * @param {string} term - Search term (default: SPX6900)
 * @param {object} params - Additional parameters including timeframe
 * @returns {Promise<GoogleTrendsData>} - Formatted trends data
 */
export const fetchGoogleTrendsWithSerpAPI = async (
  term: string = 'SPX6900 price',
  params?: TimeframeParams
): Promise<GoogleTrendsData | null> => {
  // Use 7-day timeframe to minimize API usage
  const timeframe = params?.timeframe || DEFAULT_TIMEFRAME;
  const cacheKey = `${CACHE_KEYS.GOOGLE_TRENDS}_${term}_7d`;

  // Use Upstash cache with 12-hour TTL
  const result = await withCache(cacheKey, async () => {
    console.log(`🚨 SerpAPI: Fetching fresh 7-day Google Trends data for: ${term} (Limited API usage!)`);

    // Determine the API endpoint based on environment
    const isProduction = process.env.NODE_ENV === 'production';
    // Use current window location for relative API paths to work with any port
    const apiEndpoint = isProduction
      ? '/api/google-trends'  // Use serverless function in production
      : `${window.location.origin}/api/google-trends`; // Use current host/port in development

    try {
      // Use the API endpoint to avoid CORS issues
      const response = await axios.get(apiEndpoint, {
        params: {
          term,
          timeframe
        }
      });

      const data = response.data;

      // Validate the response has the expected structure
      if (!data || typeof data.value === 'undefined') {
        throw new Error('Invalid response from Google Trends API');
      }

      console.log(`✅ SerpAPI: Retrieved ${data.historicalData?.length || 0} data points for ${term}`);
      console.log(`📊 Current Google Trends value for ${term}: ${data.value}`);

      return data;
    } catch (error: any) {
      // If API endpoint fails, throw error to trigger fallback
      console.error(`❌ Google Trends API request failed: ${error.message}`);
      throw error;
    }
  }, GOOGLE_TRENDS_TTL); // 12-hour cache TTL
  
  if (!result) {
    // Try fallback cache if Upstash fails
    const fallbackCacheKey = getCacheKey(term, DEFAULT_TIMEFRAME);
    const cachedData = fallbackCache.get(fallbackCacheKey);
    if (isCacheValid(cachedData)) {
      console.log(`📦 Using fallback cache for Google Trends: ${term}`);
      return cachedData!.data;
    }

    // Return null to indicate API failure - don't use misleading fallback data
    console.error(`❌ Google Trends API failed for "${term}" - no cached data available`);
    // Return null to trigger error state in UI
    return null;
  }

  // Store in fallback cache as backup
  const fallbackCacheKey = getCacheKey(term, DEFAULT_TIMEFRAME);
  fallbackCache.set(fallbackCacheKey, {
    timestamp: Date.now(),
    data: result
  });

  return result;
};
